import { nullable, type z } from 'zod/mini';
import {
  firewallView,
  type FirewallPolicy,
  type FirewallTargetId,
  type FirewallView,
} from './firewall-types.js';
import {
  apiTokenList,
  createdApiToken,
  alarm,
  alarmList,
  configVersion,
  createdEdge,
  enrollmentToken,
  lifecycleResult,
  vpnKey,
  discoveryRunList,
  discoveryRunResponse,
  edgeConfig,
  edgeImages,
  edgeList,
  edgeUpdate,
  edgeUpdates,
  edgeObjects,
  errorBody,
  bulkLive,
  bulkPointEdit,
  pointEdit,
  tagList,
  liveValue,
  lorawanApplication,
  lorawanApplicationList,
  lorawanDeviceDetail,
  lorawanGateway,
  lorawanGatewayList,
  lorawanProfiles,
  lorawanSetup,
  siteLorawan,
  point,
  pointList,
  siteList,
  meResponse,
  managedUser,
  tenantInfo,
  tenantList,
  managedUserList,
  valuesResponse,
  holidayList,
  ruleDetail,
  ruleEventList,
  rulePointList,
  siteRules,
  rule,
  type CreateRuleBody,
  type Holiday,
  type Rule,
  type RuleDetail,
  type RuleEvent,
  type RuleEventQuery,
  type RulePoint,
  type SiteRules,
  type UpdateRuleBody,
  type ApiToken,
  type BulkPointEditBody,
  type PointEdit,
  type PointEditBody,
  type TagCount,
  type TagQuery,
  type Alarm,
  type AlarmList,
  type AlarmQuery,
  type ConfigBody,
  type ConfigVersion,
  type CreateEdgeBody,
  type CreatedEdge,
  type EnrollmentToken,
  type LifecycleResult,
  type VpnKey,
  type CreateApiTokenBody,
  type CreatedApiToken,
  type DiscoveryRun,
  type DiscoveryScope,
  type Edge,
  type EdgeConfig,
  type EdgeImages,
  type EdgeUpdate,
  type EdgeUpdates,
  type EdgeObjects,
  type LiveValue,
  type LorawanApplication,
  type LorawanApplicationList,
  type CreateLorawanDeviceBody,
  type LorawanDeviceDetail,
  type LorawanGateway,
  type LorawanGatewayBody,
  type LorawanGatewayList,
  type LorawanProfiles,
  type LorawanSetup,
  type UpdateLorawanDeviceBody,
  type SiteLorawan,
  type Point,
  type PointList,
  type PointQuery,
  type ResolutionParam,
  type Site,
  type User,
  type ManagedUser,
  type TenantInfo,
  ALL_PERMISSIONS,
  type CreateUserBody,
  type UpdateUserBody,
  type ValuesResponse,
} from './types.js';
import {
  applyResult,
  etsImportResult,
  etsPreview,
  knxDptList,
  knxState,
  mbusState,
  protocolRunResponse,
  revisionResult,
  type ApplyResult,
  type ConverterBody,
  type EtsImportResult,
  type EtsPreview,
  type EtsUpload,
  type GroupAddressBody,
  type InterfaceBody,
  type KnxDpt,
  type KnxState,
  type MbusState,
  type MeterBody,
  type ProtocolName,
  type ProtocolRun,
  type ScanBody,
} from './protocol-types.js';

export const API_PREFIX = '/api/v1';

/** A non-2xx response; `message` is the API's `{error}` text when present. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The parsed error body (`{error, code?, …}`), when there was one. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The machine-readable `code` of the error body, e.g. `stale_selection`. */
  get code(): string | null {
    const c = (this.body as { code?: unknown } | null | undefined)?.code;
    return typeof c === 'string' ? c : null;
  }
}

/** 401: no or expired session. The app shows the login page. */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Not signed in') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

export interface ApiClientOptions {
  /** Origin prefix, '' for same-origin (default). */
  baseUrl?: string;
  fetch?: typeof fetch;
  /** Called on every 401 except from login itself. */
  onUnauthorized?: () => void;
}

type QueryValue = string | number | readonly string[] | undefined;

/** Query string; arrays repeat the key (`tag=a&tag=b`), empty values are left out. */
export function buildQuery(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value as readonly string[]) if (v !== '') search.append(key, v);
    } else search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export interface ApiClient {
  login(email: string, password: string): Promise<User>;
  logout(): Promise<void>;
  /** The signed-in user with roles and permissions, or null without a session. */
  me(): Promise<User | null>;
  sites(): Promise<Site[]>;
  edges(siteId: string): Promise<Edge[]>;
  /** LoRaWAN devices and mapped applications of a site (WP9). */
  siteLorawan(siteId: string, signal?: AbortSignal): Promise<SiteLorawan>;
  /** ChirpStack applications with their site mapping; admin. */
  lorawanApplications(signal?: AbortSignal): Promise<LorawanApplicationList>;
  /** Maps a ChirpStack application to a site; admin. */
  mapLorawanApplication(applicationId: string, siteId: string): Promise<LorawanApplication>;
  /** Removes an application's site mapping; admin. */
  unmapLorawanApplication(applicationId: string): Promise<void>;
  /** The site's ChirpStack tenant and application (provisioning); admin. */
  lorawanSetup(siteId: string, signal?: AbortSignal): Promise<LorawanSetup>;
  /** Creates (or adopts) the site's ChirpStack application; admin. */
  setupLorawan(siteId: string, applicationId?: string): Promise<LorawanSetup>;
  /** Built-in profile catalog and the site's ChirpStack device profiles; admin. */
  lorawanProfiles(siteId: string, signal?: AbortSignal): Promise<LorawanProfiles>;
  /** Adds an OTAA sensor in ChirpStack; admin. The AppKey is never returned. */
  createLorawanDevice(siteId: string, body: CreateLorawanDeviceBody): Promise<LorawanDeviceDetail>;
  updateLorawanDevice(
    siteId: string,
    devEui: string,
    body: UpdateLorawanDeviceBody,
  ): Promise<LorawanDeviceDetail>;
  deleteLorawanDevice(siteId: string, devEui: string): Promise<void>;
  lorawanGateways(siteId: string, signal?: AbortSignal): Promise<LorawanGatewayList>;
  createLorawanGateway(siteId: string, body: LorawanGatewayBody): Promise<LorawanGateway>;
  updateLorawanGateway(
    siteId: string,
    gatewayEui: string,
    body: LorawanGatewayBody,
  ): Promise<LorawanGateway>;
  deleteLorawanGateway(siteId: string, gatewayEui: string): Promise<void>;
  /** Creates an edge (and its site when the group id is new); admin only. */
  createEdge(body: CreateEdgeBody): Promise<CreatedEdge>;
  /** A one-time enrollment token with the install command (shown once, 24 h). */
  createEnrollmentToken(edgeId: string): Promise<EnrollmentToken>;
  /** A single-use VPN key for an enrolled edge. */
  createVpnKey(edgeId: string): Promise<VpnKey>;
  /** Bans the edge in EMQX and removes it from the VPN; repeat to retry failed steps. */
  disableEdge(edgeId: string): Promise<LifecycleResult>;
  enableEdge(edgeId: string): Promise<LifecycleResult>;
  points(query: PointQuery, signal?: AbortSignal): Promise<PointList>;
  point(id: string): Promise<Point>;
  live(id: string): Promise<LiveValue | null>;
  values(
    id: string,
    range: { from: number; to: number; resolution?: ResolutionParam },
    signal?: AbortSignal,
  ): Promise<ValuesResponse>;
  /** Discovered objects of an edge with the current selection. */
  edgeObjects(edgeId: string, signal?: AbortSignal): Promise<EdgeObjects>;
  edgeConfig(edgeId: string, signal?: AbortSignal): Promise<EdgeConfig>;
  /** Sends a new point selection to the edge; the version starts as `sent`. */
  putEdgeConfig(edgeId: string, body: ConfigBody): Promise<ConfigVersion>;
  startDiscovery(edgeId: string, scope?: DiscoveryScope): Promise<DiscoveryRun>;
  /** Discovery runs of an edge, newest first. */
  discoveryRuns(edgeId: string, signal?: AbortSignal): Promise<DiscoveryRun[]>;
  /** Agent versions in the registry. */
  edgeImages(signal?: AbortSignal): Promise<EdgeImages>;
  edgeUpdates(edgeId: string, signal?: AbortSignal): Promise<EdgeUpdates>;
  /** Sends an agent update; it starts as `sent` until the edge reports. */
  startUpdate(edgeId: string, tag: string): Promise<EdgeUpdate>;
  /** Host firewall of central or an edge (ADR 0009). */
  firewall(target: FirewallTargetId, signal?: AbortSignal): Promise<FirewallView>;
  /** Stores the next version; the host applies it and waits for `confirmFirewall`. */
  putFirewall(
    target: FirewallTargetId,
    body: { policy: FirewallPolicy; base_version: number | null },
  ): Promise<FirewallView>;
  /** Keeps a pending version (the browser still reaches the API). */
  confirmFirewall(target: FirewallTargetId, version: number): Promise<FirewallView>;
  /** API tokens of the tenant, newest first (no secrets). */
  apiTokens(signal?: AbortSignal): Promise<ApiToken[]>;
  /** Creates a token; the response carries the secret, shown only once. */
  createApiToken(body: CreateApiTokenBody): Promise<CreatedApiToken>;
  revokeApiToken(id: string): Promise<void>;
  /** Changes the signed-in user's password; their other sessions end. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  /** Users the caller manages (platform staff: all, or of one tenant). */
  users(signal?: AbortSignal, tenantId?: string): Promise<ManagedUser[]>;
  /** Tenants with their sites (platform staff). */
  tenants(signal?: AbortSignal): Promise<TenantInfo[]>;
  createTenant(name: string, slug: string): Promise<TenantInfo>;
  /** Moves a site to another tenant (platform staff). */
  moveSite(siteId: string, tenantId: string): Promise<void>;
  createUser(body: CreateUserBody): Promise<ManagedUser>;
  /** Renames, disables or enables a user; disabling ends their sessions. */
  updateUser(id: string, body: UpdateUserBody): Promise<ManagedUser>;
  /** Sets a new password for a user; their sessions end. */
  resetUserPassword(id: string, newPassword: string): Promise<void>;
  deleteUser(id: string): Promise<void>;
  /** Current values of many points (bulk); points without a value are left out. */
  liveValues(ids: readonly string[], signal?: AbortSignal): Promise<LiveValue[]>;
  /** Tags in use with their point counts. */
  tags(query: TagQuery, signal?: AbortSignal): Promise<TagCount[]>;
  /** Edits tags (replaced as a whole) and limits of one point. */
  updatePoint(id: string, body: PointEditBody): Promise<PointEdit>;
  /** Edits tags and limits of many points. */
  updatePoints(body: BulkPointEditBody): Promise<PointEdit[]>;
  /** Alarms, open first; `open` counts the open ones (for the badge). */
  alarms(query: AlarmQuery, signal?: AbortSignal): Promise<AlarmList>;
  /** Acknowledges an alarm; it stays open until its condition clears. */
  ackAlarm(id: string): Promise<Alarm>;
  /** Rules of a site with the bundle state per edge and the site holidays. */
  siteRules(siteId: string, signal?: AbortSignal): Promise<SiteRules>;
  rule(siteId: string, ruleId: string, signal?: AbortSignal): Promise<RuleDetail>;
  /** Creates a rule; central pushes a new rules bundle to its edge. */
  createRule(siteId: string, body: CreateRuleBody): Promise<Rule>;
  /** New version of a rule (409 `stale_rule` when `base_version` is outdated). */
  updateRule(siteId: string, ruleId: string, body: UpdateRuleBody): Promise<Rule>;
  deleteRule(siteId: string, ruleId: string): Promise<void>;
  /** Events the edges reported for the site's rules, newest first. */
  ruleEvents(siteId: string, query: RuleEventQuery, signal?: AbortSignal): Promise<RuleEvent[]>;
  putHolidays(siteId: string, holidays: Holiday[]): Promise<Holiday[]>;
  /** Points a rule on this edge may read or act on (incl. virtual points). */
  rulePoints(siteId: string, edgeId: string, signal?: AbortSignal): Promise<RulePoint[]>;

  // M-Bus and KNX managed from central (ADR 0008). Draft edits answer the
  // new revision; 409 `stale_config` when `base_revision` is outdated.
  edgeMbus(edgeId: string, signal?: AbortSignal): Promise<MbusState>;
  putMbusConverter(edgeId: string, id: string, body: ConverterBody): Promise<number>;
  deleteMbusConverter(edgeId: string, id: string, baseRevision: number | null): Promise<number>;
  putMbusMeter(edgeId: string, deviceId: string, body: MeterBody): Promise<number>;
  deleteMbusMeter(edgeId: string, deviceId: string, baseRevision: number | null): Promise<number>;
  /** Scans a converter of the draft on the edge (read-only). */
  startMbusScan(edgeId: string, body: ScanBody): Promise<ProtocolRun>;
  edgeKnx(edgeId: string, signal?: AbortSignal): Promise<KnxState>;
  /** DPTs the edge decodes (for pickers). */
  knxDpts(signal?: AbortSignal): Promise<KnxDpt[]>;
  putKnxInterface(edgeId: string, id: string, body: InterfaceBody): Promise<number>;
  deleteKnxInterface(edgeId: string, id: string, baseRevision: number | null): Promise<number>;
  addKnxGroupAddresses(
    edgeId: string,
    ifaceId: string,
    body: { items: GroupAddressBody[]; replace?: boolean; base_revision?: number | null },
  ): Promise<number>;
  putKnxGroupAddress(
    edgeId: string,
    ifaceId: string,
    address: string,
    body: Omit<GroupAddressBody, 'address'> & { base_revision?: number | null },
  ): Promise<number>;
  deleteKnxGroupAddress(
    edgeId: string,
    ifaceId: string,
    address: string,
    baseRevision: number | null,
  ): Promise<number>;
  previewEts(edgeId: string, ifaceId: string, upload: EtsUpload): Promise<EtsPreview>;
  importEts(
    edgeId: string,
    ifaceId: string,
    body: EtsUpload & { addresses?: string[]; overwrite: boolean; base_revision?: number | null },
  ): Promise<EtsImportResult>;
  /** Asks the edge for the group addresses it hears but has not configured. */
  requestKnxSeen(edgeId: string, ifaceId?: string): Promise<ProtocolRun>;
  /** Sends the draft as a new config bundle (unchanged drafts store nothing). */
  applyProtocol(
    edgeId: string,
    protocol: ProtocolName,
    revision: number | null,
  ): Promise<ApplyResult>;
  /** Hands the protocol back to the edge's file (takes effect with the next apply). */
  releaseProtocol(
    edgeId: string,
    protocol: ProtocolName,
    baseRevision: number | null,
  ): Promise<number>;
}

/** URL form of a group address: `1/2/3` → `1-2-3`. */
export const gaParam = (address: string) => address.replaceAll('/', '-');

const nullableLive = nullable(liveValue);

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? '';
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  async function request(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    init: { body?: unknown; signal?: AbortSignal | undefined; isLogin?: boolean } = {},
  ): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    const reqInit: RequestInit = { method, headers, credentials: 'same-origin' };
    if (init.body !== undefined) {
      headers['content-type'] = 'application/json';
      reqInit.body = JSON.stringify(init.body);
    }
    if (init.signal) reqInit.signal = init.signal;
    const res = await doFetch(`${baseUrl}${API_PREFIX}${path}`, reqInit);
    if (res.status === 204) return undefined;
    const text = await res.text();
    let body: unknown = undefined;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        if (res.ok) throw new ApiError(res.status, 'Invalid JSON from the server');
      }
    }
    if (!res.ok) {
      const parsed = errorBody.safeParse(body);
      const message = parsed.success ? parsed.data.error : `HTTP ${res.status}`;
      if (res.status === 401) {
        if (!init.isLogin) options.onUnauthorized?.();
        throw new UnauthorizedError(message);
      }
      throw new ApiError(res.status, message, body);
    }
    return body;
  }

  async function get<S extends z.ZodMiniType>(
    schema: S,
    path: string,
    signal?: AbortSignal,
  ): Promise<z.infer<S>> {
    const body = await request('GET', path, { signal });
    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      throw new ApiError(200, `Unexpected response from ${path}: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async function send<S extends z.ZodMiniType>(
    schema: S,
    method: 'POST' | 'PUT' | 'PATCH',
    path: string,
    body: unknown,
  ): Promise<z.infer<S>> {
    const res = await request(method, path, { body });
    const parsed = schema.safeParse(res);
    if (!parsed.success) {
      throw new ApiError(200, `Unexpected response from ${path}: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  const enc = encodeURIComponent;

  /** DELETE that answers `{revision}` (draft edits, ADR 0008). */
  async function delRevision(path: string, baseRevision: number | null): Promise<number> {
    const q = buildQuery({ base_revision: baseRevision ?? undefined });
    const parsed = revisionResult.safeParse(await request('DELETE', `${path}${q}`));
    if (!parsed.success) throw new ApiError(200, `Unexpected response from ${path}`);
    return parsed.data.revision;
  }
  const revisionOf = async (p: Promise<{ revision: number }>) => (await p).revision;
  const edgePath = (edgeId: string, rest: string) => `/edges/${enc(edgeId)}${rest}`;
  const firewallPath = (t: FirewallTargetId) =>
    t.kind === 'central' ? '/firewall/central' : edgePath(t.edgeId, '/firewall');

  const me = async (): Promise<User | null> => {
    try {
      const body = meResponse.parse(await request('GET', '/auth/me', { isLogin: true }));
      if (!body.user) return null;
      return { ...body.user, permissions: body.permissions ?? ALL_PERMISSIONS };
    } catch (err) {
      if (err instanceof UnauthorizedError) return null;
      throw err;
    }
  };

  return {
    async login(email, password) {
      await request('POST', '/auth/login', {
        body: { email, password },
        isLogin: true,
      });
      // Roles and permissions come from /auth/me (the same call as on reload).
      const user = await me();
      if (!user) throw new UnauthorizedError();
      return user;
    },
    async logout() {
      await request('POST', '/auth/logout');
    },
    me,
    async sites() {
      return (await get(siteList, '/sites')).items;
    },
    async edges(siteId) {
      return (await get(edgeList, `/sites/${enc(siteId)}/edges`)).items;
    },
    siteLorawan(siteId, signal) {
      return get(siteLorawan, `/sites/${enc(siteId)}/lorawan`, signal);
    },
    lorawanApplications(signal) {
      return get(lorawanApplicationList, '/lorawan/applications', signal);
    },
    mapLorawanApplication(applicationId, siteId) {
      return send(lorawanApplication, 'PUT', `/lorawan/applications/${enc(applicationId)}`, {
        site_id: siteId,
      });
    },
    async unmapLorawanApplication(applicationId) {
      await request('DELETE', `/lorawan/applications/${enc(applicationId)}`);
    },
    lorawanSetup(siteId, signal) {
      return get(lorawanSetup, `/sites/${enc(siteId)}/lorawan/setup`, signal);
    },
    setupLorawan(siteId, applicationId) {
      return send(lorawanSetup, 'POST', `/sites/${enc(siteId)}/lorawan/setup`, {
        ...(applicationId !== undefined && { application_id: applicationId }),
      });
    },
    lorawanProfiles(siteId, signal) {
      return get(lorawanProfiles, `/sites/${enc(siteId)}/lorawan/device-profiles`, signal);
    },
    createLorawanDevice(siteId, body) {
      return send(lorawanDeviceDetail, 'POST', `/sites/${enc(siteId)}/lorawan/devices`, body);
    },
    updateLorawanDevice(siteId, devEui, body) {
      return send(
        lorawanDeviceDetail,
        'PATCH',
        `/sites/${enc(siteId)}/lorawan/devices/${enc(devEui)}`,
        body,
      );
    },
    async deleteLorawanDevice(siteId, devEui) {
      await request('DELETE', `/sites/${enc(siteId)}/lorawan/devices/${enc(devEui)}`);
    },
    lorawanGateways(siteId, signal) {
      return get(lorawanGatewayList, `/sites/${enc(siteId)}/lorawan/gateways`, signal);
    },
    createLorawanGateway(siteId, body) {
      return send(lorawanGateway, 'POST', `/sites/${enc(siteId)}/lorawan/gateways`, body);
    },
    updateLorawanGateway(siteId, gatewayEui, body) {
      return send(
        lorawanGateway,
        'PATCH',
        `/sites/${enc(siteId)}/lorawan/gateways/${enc(gatewayEui)}`,
        body,
      );
    },
    async deleteLorawanGateway(siteId, gatewayEui) {
      await request('DELETE', `/sites/${enc(siteId)}/lorawan/gateways/${enc(gatewayEui)}`);
    },
    createEdge(body) {
      return send(createdEdge, 'POST', '/edges', body);
    },
    createEnrollmentToken(edgeId) {
      return send(enrollmentToken, 'POST', `/edges/${enc(edgeId)}/enrollment-tokens`, {});
    },
    createVpnKey(edgeId) {
      return send(vpnKey, 'POST', `/edges/${enc(edgeId)}/vpn-key`, {});
    },
    disableEdge(edgeId) {
      return send(lifecycleResult, 'POST', `/edges/${enc(edgeId)}/disable`, {});
    },
    enableEdge(edgeId) {
      return send(lifecycleResult, 'POST', `/edges/${enc(edgeId)}/enable`, {});
    },
    points(query, signal) {
      return get(pointList, `/points${buildQuery({ ...query })}`, signal);
    },
    point(id) {
      return get(point, `/points/${enc(id)}`);
    },
    live(id) {
      return get(nullableLive, `/points/${enc(id)}/live`);
    },
    values(id, range, signal) {
      const q = buildQuery({
        from: range.from,
        to: range.to,
        resolution: range.resolution ?? 'auto',
      });
      return get(valuesResponse, `/points/${enc(id)}/values${q}`, signal);
    },
    edgeObjects(edgeId, signal) {
      return get(edgeObjects, `/edges/${enc(edgeId)}/objects`, signal);
    },
    edgeConfig(edgeId, signal) {
      return get(edgeConfig, `/edges/${enc(edgeId)}/config`, signal);
    },
    putEdgeConfig(edgeId, body) {
      return send(configVersion, 'PUT', `/edges/${enc(edgeId)}/config`, body);
    },
    async startDiscovery(edgeId, scope) {
      const body = scope ? { edge_id: edgeId, scope } : { edge_id: edgeId };
      return (await send(discoveryRunResponse, 'POST', '/discovery-runs', body)).run;
    },
    async discoveryRuns(edgeId, signal) {
      return (await get(discoveryRunList, `/edges/${enc(edgeId)}/discovery-runs`, signal)).items;
    },
    edgeImages(signal) {
      return get(edgeImages, '/edge-images', signal);
    },
    edgeUpdates(edgeId, signal) {
      return get(edgeUpdates, `/edges/${enc(edgeId)}/updates`, signal);
    },
    startUpdate(edgeId, tag) {
      return send(edgeUpdate, 'POST', `/edges/${enc(edgeId)}/updates`, { tag });
    },
    firewall(target, signal) {
      return get(firewallView, firewallPath(target), signal);
    },
    putFirewall(target, body) {
      return send(firewallView, 'PUT', firewallPath(target), body);
    },
    confirmFirewall(target, version) {
      return send(firewallView, 'POST', `${firewallPath(target)}/confirm`, { version });
    },
    async apiTokens(signal) {
      return (await get(apiTokenList, '/api-tokens', signal)).items;
    },
    createApiToken(body) {
      return send(createdApiToken, 'POST', '/api-tokens', body);
    },
    async revokeApiToken(id) {
      await request('DELETE', `/api-tokens/${enc(id)}`);
    },
    async changePassword(currentPassword, newPassword) {
      await request('POST', '/auth/password', {
        body: { current_password: currentPassword, new_password: newPassword },
      });
    },
    async users(signal, tenantId) {
      const qs = tenantId ? `?tenant_id=${enc(tenantId)}` : '';
      return (await get(managedUserList, `/users${qs}`, signal)).items;
    },
    async tenants(signal) {
      return (await get(tenantList, '/tenants', signal)).items;
    },
    createTenant(name, slug) {
      return send(tenantInfo, 'POST', '/tenants', { name, slug });
    },
    async moveSite(siteId, tenantId) {
      await request('PUT', `/sites/${enc(siteId)}/tenant`, { body: { tenant_id: tenantId } });
    },
    createUser(body) {
      return send(managedUser, 'POST', '/users', body);
    },
    updateUser(id, body) {
      return send(managedUser, 'PATCH', `/users/${enc(id)}`, body);
    },
    async resetUserPassword(id, newPassword) {
      await request('POST', `/users/${enc(id)}/password`, {
        body: { new_password: newPassword },
      });
    },
    async deleteUser(id) {
      await request('DELETE', `/users/${enc(id)}`);
    },
    async liveValues(ids, signal) {
      if (ids.length === 0) return [];
      const res = await get(
        bulkLive,
        `/points/live${buildQuery({ points: ids.join(',') })}`,
        signal,
      );
      return res.items.flatMap((v) =>
        v.ts === null || v.q === null ? [] : [{ ...v, ts: v.ts, q: v.q }],
      );
    },
    async tags(query, signal) {
      return (await get(tagList, `/tags${buildQuery({ ...query })}`, signal)).items;
    },
    updatePoint(id, body) {
      return send(pointEdit, 'PATCH', `/points/${enc(id)}`, body);
    },
    async updatePoints(body) {
      return (await send(bulkPointEdit, 'POST', '/points/bulk', body)).items;
    },
    alarms(query, signal) {
      return get(alarmList, `/alarms${buildQuery({ ...query })}`, signal);
    },
    ackAlarm(id) {
      return send(alarm, 'POST', `/alarms/${enc(id)}/ack`, {});
    },
    siteRules(siteId, signal) {
      return get(siteRules, `/sites/${enc(siteId)}/rules`, signal);
    },
    rule(siteId, ruleId, signal) {
      return get(ruleDetail, `/sites/${enc(siteId)}/rules/${enc(ruleId)}`, signal);
    },
    createRule(siteId, body) {
      return send(rule, 'POST', `/sites/${enc(siteId)}/rules`, body);
    },
    updateRule(siteId, ruleId, body) {
      return send(rule, 'PUT', `/sites/${enc(siteId)}/rules/${enc(ruleId)}`, body);
    },
    async deleteRule(siteId, ruleId) {
      await request('DELETE', `/sites/${enc(siteId)}/rules/${enc(ruleId)}`);
    },
    async ruleEvents(siteId, query, signal) {
      const q = buildQuery({ ...query });
      return (await get(ruleEventList, `/sites/${enc(siteId)}/rule-events${q}`, signal)).items;
    },
    async putHolidays(siteId, holidays) {
      const res = await send(holidayList, 'PUT', `/sites/${enc(siteId)}/holidays`, { holidays });
      return res.holidays;
    },
    async rulePoints(siteId, edgeId, signal) {
      const q = buildQuery({ edge_id: edgeId });
      return (await get(rulePointList, `/sites/${enc(siteId)}/rule-points${q}`, signal)).items;
    },
    edgeMbus(edgeId, signal) {
      return get(mbusState, edgePath(edgeId, '/mbus'), signal);
    },
    putMbusConverter(edgeId, id, body) {
      return revisionOf(
        send(revisionResult, 'PUT', edgePath(edgeId, `/mbus/converters/${enc(id)}`), body),
      );
    },
    deleteMbusConverter(edgeId, id, baseRevision) {
      return delRevision(edgePath(edgeId, `/mbus/converters/${enc(id)}`), baseRevision);
    },
    putMbusMeter(edgeId, deviceId, body) {
      return revisionOf(
        send(revisionResult, 'PUT', edgePath(edgeId, `/mbus/meters/${enc(deviceId)}`), body),
      );
    },
    deleteMbusMeter(edgeId, deviceId, baseRevision) {
      return delRevision(edgePath(edgeId, `/mbus/meters/${enc(deviceId)}`), baseRevision);
    },
    async startMbusScan(edgeId, body) {
      return (await send(protocolRunResponse, 'POST', edgePath(edgeId, '/mbus/scans'), body)).run;
    },
    edgeKnx(edgeId, signal) {
      return get(knxState, edgePath(edgeId, '/knx'), signal);
    },
    async knxDpts(signal) {
      return (await get(knxDptList, '/knx/dpts', signal)).items;
    },
    putKnxInterface(edgeId, id, body) {
      return revisionOf(
        send(revisionResult, 'PUT', edgePath(edgeId, `/knx/interfaces/${enc(id)}`), body),
      );
    },
    deleteKnxInterface(edgeId, id, baseRevision) {
      return delRevision(edgePath(edgeId, `/knx/interfaces/${enc(id)}`), baseRevision);
    },
    addKnxGroupAddresses(edgeId, ifaceId, body) {
      const path = edgePath(edgeId, `/knx/interfaces/${enc(ifaceId)}/group-addresses`);
      return revisionOf(send(revisionResult, 'POST', path, body));
    },
    putKnxGroupAddress(edgeId, ifaceId, address, body) {
      const path = edgePath(
        edgeId,
        `/knx/interfaces/${enc(ifaceId)}/group-addresses/${enc(gaParam(address))}`,
      );
      return revisionOf(send(revisionResult, 'PUT', path, body));
    },
    deleteKnxGroupAddress(edgeId, ifaceId, address, baseRevision) {
      const path = edgePath(
        edgeId,
        `/knx/interfaces/${enc(ifaceId)}/group-addresses/${enc(gaParam(address))}`,
      );
      return delRevision(path, baseRevision);
    },
    previewEts(edgeId, ifaceId, upload) {
      const path = edgePath(edgeId, `/knx/interfaces/${enc(ifaceId)}/ets/preview`);
      return send(etsPreview, 'POST', path, upload);
    },
    importEts(edgeId, ifaceId, body) {
      const path = edgePath(edgeId, `/knx/interfaces/${enc(ifaceId)}/ets/import`);
      return send(etsImportResult, 'POST', path, body);
    },
    async requestKnxSeen(edgeId, ifaceId) {
      const body = ifaceId === undefined ? {} : { interface: ifaceId };
      return (await send(protocolRunResponse, 'POST', edgePath(edgeId, '/knx/seen-requests'), body))
        .run;
    },
    applyProtocol(edgeId, protocol, revision) {
      return send(applyResult, 'POST', edgePath(edgeId, `/${protocol}/apply`), { revision });
    },
    releaseProtocol(edgeId, protocol, baseRevision) {
      return delRevision(edgePath(edgeId, `/${protocol}`), baseRevision);
    },
  };
}

/** WebSocket URL for live values of the given points, same origin as the page. */
export function liveSocketUrl(
  pointIds: readonly string[],
  location: Pick<Location, 'protocol' | 'host'>,
): string {
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ids = [...pointIds].sort().map(encodeURIComponent).join(',');
  return `${scheme}//${location.host}${API_PREFIX}/live?points=${ids}`;
}
